/**
 * SPEC.md §3, §6, §9 — the topbar's Copy menu, disabled since M0. M5.4
 * (issue #24) turns it into a real dropdown, mirroring `NewMenu.tsx`'s and
 * `ExportMenu.tsx`'s shape and built on the same `useMenu` hook (M5.1,
 * issue #21), with six items each a plain string transform of the current
 * source and/or the render loop's SVG markup — no new render. M5.5 (issue
 * #25) adds a seventh, mermaid.live link — the one target that's a genuine
 * link to an external site rather than a local transform, added last per
 * SPEC.md §6's own ordering ("a compatibility nicety, do it last").
 *
 * Positioned at the topbar's right edge like `ExportMenu`, so its menu
 * opens right-aligned rather than overlapping the Share/Export buttons
 * beside it.
 */
import { ChevronDown, Copy as CopyIcon } from 'lucide-react';
import {
  toDataUri,
  toHtmlEmbed,
  toInlineSvg,
  toMarkdownFence,
  toMdx,
  toMermaidLiveLink,
  toReactComponent,
  type CopyAsContext,
} from '../lib/export';
import { useMenu } from './useMenu';

interface CopyMenuProps {
  source: string;
  /** The render loop's current SVG markup — null until the first successful render. */
  svg: string | null;
  /** Called with a §9-style confirmation string ("Copied as Markdown.") after a successful copy. */
  onCopy: (message: string) => void;
}

interface CopyAsTarget {
  id: string;
  label: string;
  generate: (ctx: CopyAsContext) => string;
}

const COPY_AS_TARGETS: CopyAsTarget[] = [
  { id: 'markdown', label: 'Markdown', generate: toMarkdownFence },
  { id: 'html-embed', label: 'HTML embed', generate: toHtmlEmbed },
  { id: 'inline-svg', label: 'Inline SVG', generate: toInlineSvg },
  { id: 'data-uri', label: 'Data URI', generate: toDataUri },
  { id: 'react-component', label: 'React component', generate: toReactComponent },
  { id: 'mdx', label: 'MDX/Docusaurus', generate: toMdx },
  { id: 'mermaid-live', label: 'mermaid.live link', generate: toMermaidLiveLink },
];

export default function CopyMenu({ source, svg, onCopy }: CopyMenuProps) {
  const { isOpen, containerRef, toggle, close } = useMenu<HTMLDivElement>();

  function handleCopy(target: CopyAsTarget) {
    if (svg === null) return;
    close();

    let text: string;
    try {
      text = target.generate({ source, svg });
    } catch (err) {
      console.error('Copy-as generation failed:', err);
      return;
    }

    navigator.clipboard
      .writeText(text)
      .then(() => onCopy(`Copied as ${target.label}.`))
      .catch((err: unknown) => console.error('Copy to clipboard failed:', err));
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={toggle}
        disabled={svg === null}
        className="flex items-center gap-1.5 rounded-sm px-2 py-1 text-xs text-text-ink hover:bg-ink-3 disabled:cursor-not-allowed disabled:text-ink-4 disabled:hover:bg-transparent"
      >
        <CopyIcon size={13} aria-hidden />
        Copy
        <ChevronDown size={12} aria-hidden />
      </button>

      {isOpen && (
        <div
          role="menu"
          aria-label="Copy as"
          className="absolute right-0 top-full z-10 mt-1 w-56 rounded-sm border border-ink-3 bg-ink-2 py-1 shadow-lg"
        >
          {COPY_AS_TARGETS.map((target) => (
            <button
              key={target.id}
              type="button"
              role="menuitem"
              onClick={() => handleCopy(target)}
              className="block w-full px-3 py-1.5 text-left text-xs text-text-ink hover:bg-ink-3"
            >
              {target.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
